import axios from 'axios';
import { message, Modal } from 'antd';
import { getToken, clearAuth } from './auth';

// 创建通用的axios实例创建函数
export const createAxiosInstance = (config = {}) => {
  const defaultConfig = {
    baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
    timeout: 180000
  };

  const mergedConfig = { ...defaultConfig, ...config };
  const service = axios.create(mergedConfig);

  // 请求拦截器
  service.interceptors.request.use(
    config => {
      // 从auth工具获取token
      const token = getToken();
      if (token) {
        // 设置统一的token请求头
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Type'] = 'FISH';
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  service.interceptors.response.use(
    response => {
      // 如果是下载文件直接返回
      if (response.config.responseType === 'blob') {
        return response;
      }

      const res = response.data;

      // 根据自定义错误码判断请求是否成功
      if (res.code === 200) {
        // 特殊处理权限接口
        if (response.config.url.includes('/permissions/user')) {
          // 确保返回的权限数据是数组
          if (res.data && !Array.isArray(res.data)) {
            // 尝试转换为数组或使用默认空数组
            res.data = Array.isArray(res.data) ? res.data : [];
          }
        }
        return res;
      } else {
        // 处理业务错误
        message.error(res.message || res.msg || '操作失败');

        // 处理特定错误码
        if (res.code === 401 || res.code === '401') {
          // 未登录或token过期
          handleUnauthorized();
        }

        return Promise.reject(new Error(res.message || res.msg || '操作失败'));
      }
    },
    error => {
      console.error('响应错误:', error);

      // 处理HTTP错误状态码
      const { response } = error;
      if (response) {
        const { status, data } = response;

        switch (status) {
          case 401:
            handleUnauthorized();
            break;
          case 403:
            message.error('没有权限访问该资源');
            // 跳转到403页面
            if (window.location.pathname !== '/403') {
              window.location.href = '/403';
            }
            break;
          case 404:
            message.error('请求的资源不存在');
            break;
          case 500:
            message.error('服务器错误，请联系管理员');
            break;
          default:
            message.error(data?.message || `请求失败(${status})`);
        }
      } else {
        message.error('网络异常，请检查您的网络连接');
      }

      return Promise.reject(error);
    }
  );

  return service;
};

// 默认的axios实例
const service = createAxiosInstance();

/**
 * 处理token过期
 * 可以在这里添加token刷新逻辑
 */
const handleTokenExpired = () => {
  // 清除本地存储的token和用户信息
  clearAuth();

  // 如果有需要，可以在这里添加token刷新逻辑
  // 例如：尝试使用refreshToken获取新的token

  // 重定向到登录页
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * 处理401未授权错误
 * 弹出确认弹窗，确认后跳转到登录页
 */
const handleUnauthorized = () => {
  Modal.confirm({
    title: '登录信息过期',
    content: '您的登录信息已过期，请重新登录',
    okText: '确定',
    cancelText: '取消',
    onOk: () => {
      // 清除本地存储的token和用户信息
      clearAuth();
      // 跳转到登录页
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    },
    onCancel: () => {
      // 取消时也清除认证信息并跳转（通常用户取消也应该跳转）
      clearAuth();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  });
};

export default service; 